import {ApiFilterType} from "./ViewSettings";
import {FilterTypes} from "../../helpers/FilterTypes";
import {ProjectId} from "../projects/Project";
import {projectFilterName} from "../../components/common/data-filters/ProjectFilterInput";
import {Identifier} from "./Identifier";
import {projectStageFilterName} from "../../components/common/data-filters/ProjectStageFilterInput";
import {projectStagePhaseFilterName} from "../../components/common/data-filters/ProjectStagePhaseFilterInput";

/**
 * Represents the type of a data filter, derived from the API filter type definition.
 */
export type DataFilterType = ApiFilterType

/**
 * Enumeration of all supported data filter type identifiers.
 * Used to classify filters by the kind of value they operate on.
 */
export const DataFilterTypes = {
    ID: 'ID',
    ID_STRING: 'ID_STRING',
    START_DATE: 'START_DATE',
    END_DATE: 'END_DATE',
    BOOL: 'BOOL',
    STRING: 'STRING'
} as const;

/**
 * Props for components that operate on a single active data filter.
 */
export interface SingleActiveDataFilterProp {
    /** The active data filter instance to work with. */
    filter: ActiveDataFilter<any>
}

/**
 * Props for components that operate on a list of active data filters.
 */
export interface ActiveDataFilterListProp {
    /** The array of active data filters to work with. */
    filters: ActiveDataFilter<any>[]
}

/**
 * Creates a human-readable display title for a filter by combining its name and current value.
 *
 * @param filterTitle - The label of the filter (e.g. "Project").
 * @param filterValueText - The textual representation of the selected filter value.
 * @returns A formatted string in the form "filterTitle: filterValueText".
 */
export const createFilterDisplayTitle = ( filterTitle: string, filterValueText: string ): string =>
    filterTitle + ": " + filterValueText;

/**
 * Determines whether the given active data filter is a date-based filter.
 *
 * @param filter - The active data filter to check.
 * @returns `true` if the filter type is END_DATE, START_DATE, or DATE.
 */
export const isDateFilter = ( filter: ActiveDataFilter<any> ): boolean =>
    ( filter.type === FilterTypes.END_DATE || filter.type === FilterTypes.START_DATE || filter.type === FilterTypes.DATE )

/** Prefix prepended to all data filter identifiers to namespace them. */
export const DATA_FILTER_PREFIX = "filter__";

/**
 * Callback signature for handling changes to an active data filter within a specific view.
 *
 * @typeParam U - The return type of the handler.
 */
export interface HandleActiveDataFilterChange<U> {
    /**
     * @typeParam T - The type of the filter's selected value.
     * @param viewIdentifier - The identifier of the view containing the filter.
     * @param filter - The active data filter that changed.
     * @returns A value of type U.
     */
    <T>( viewIdentifier: string, filter: ActiveDataFilter<T> ): U
}

/**
 * Represents a data filter that has been activated with a selected value.
 * Extends {@link DataFilter} with selection state and display metadata.
 *
 * @typeParam T - The type of the selected filter value.
 */
export interface ActiveDataFilter<T> extends DataFilter {
    /** The currently selected value for this filter. */
    selectedValue: T
    /** Whether the filter logic is inverted (i.e. exclude instead of include). */
    isInverted: boolean
    /** A human-readable name shown in the UI for this filter. */
    displayName: string
}

/**
 * Base definition of a data filter, identified by a unique string and a type classifier.
 */
export interface DataFilter {
    /** Unique identifier for the filter, prefixed with {@link DATA_FILTER_PREFIX}. */
    identifier: string
    /** The classification type of this filter. */
    type: DataFilterType
}

/**
 * Constructs a base {@link DataFilter} by combining the prefix with the given filter name.
 *
 * @param filterName - The raw name of the filter (without prefix).
 * @param type - The data filter type classifier.
 * @returns A new {@link DataFilter} instance.
 */
export const constructDataFilter = ( filterName: string, type: DataFilterType ): DataFilter => ({
    identifier: DATA_FILTER_PREFIX + filterName,
    type: type
});

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-level filtering.
 *
 * @param projectId - The identifier of the project to filter by.
 * @param isInverted - Whether the filter should exclude the given project. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project.
 * @see ActiveDataFilter
 */
export function constructProjectFilterActive(
    projectId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectId.rawValue
    };
}

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-stage-level filtering.
 *
 * @param projectStageId - The identifier of the project stage to filter by.
 * @param isInverted - Whether the filter should exclude the given stage. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project stage.
 * @see ActiveDataFilter
 */
export function constructStageFilterActive(
    projectStageId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectStageFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectStageId.rawValue
    };
}

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-stage-phase-level filtering.
 *
 * @param projectStagePhaseId - The identifier of the project stage phase to filter by.
 * @param isInverted - Whether the filter should exclude the given phase. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project stage phase.
 * @see ActiveDataFilter
 */
export function constructPhaseFilterActive(
    projectStagePhaseId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectStagePhaseFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectStagePhaseId.rawValue
    };
}